// Folder policy dialog (design §3.4, §10): how agents may write in a folder
// — directly, via a proposal a person approves, or not at all. A folder
// without its own policy inherits the longest matching parent (default
// `direct`); "Inherit" removes the folder's own policy.

import { useEffect, useState } from 'react'
import { Dialog } from '../ui'
import { KB_AGENT_WRITE_LABEL, type KbAgentWrite, type KbFolderPolicy } from './kb-types'

export interface KbFolderPolicyDialogProps {
  open: boolean
  onClose: () => void
  spaceLabel: string
  /** Folder prefix with trailing slash; '' = the whole space. */
  folder: string
  /** The folder's own policy, or null when it inherits. */
  value: KbAgentWrite | null
  /** What applies when `value` is null (nearest parent policy, or null = default direct). */
  inherited?: KbFolderPolicy | null
  /** null = remove the folder's own policy (inherit). */
  onSave: (agentWrite: KbAgentWrite | null) => void
  saving?: boolean
  error?: string | null
}

const OPTIONS: { value: KbAgentWrite; hint: string }[] = [
  { value: 'direct', hint: 'Every change is a revision you can roll back.' },
  { value: 'proposal', hint: 'Agent changes wait in Proposals until someone approves them.' },
  { value: 'read_only', hint: 'Agents can read pages here but not change them.' },
]

export function KbFolderPolicyDialog({
  open,
  onClose,
  spaceLabel,
  folder,
  value,
  inherited,
  onSave,
  saving,
  error,
}: KbFolderPolicyDialogProps) {
  const [choice, setChoice] = useState<KbAgentWrite | 'inherit'>(value ?? 'inherit')
  useEffect(() => {
    if (open) setChoice(value ?? 'inherit')
  }, [open, value])

  const inheritedLabel = inherited
    ? `${KB_AGENT_WRITE_LABEL[inherited.agentWrite]} (from ${inherited.folder || 'the space'})`
    : KB_AGENT_WRITE_LABEL.direct + ' (default)'

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title="Agent policy"
      description={
        <>
          {spaceLabel} / <span className="font-mono">{folder || '(all pages)'}</span>
          <span className="block text-sm text-ink-3">People can always edit directly.</span>
        </>
      }
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          onSave(choice === 'inherit' ? null : choice)
        }}
        className="space-y-4"
      >
        <fieldset className="space-y-2">
          <legend className="sr-only">How agents may write here</legend>
          {folder ? (
            <Option
              checked={choice === 'inherit'}
              onChange={() => setChoice('inherit')}
              label="Inherit"
              hint={inheritedLabel}
            />
          ) : null}
          {OPTIONS.map((option) => (
            <Option
              key={option.value}
              checked={choice === option.value}
              onChange={() => setChoice(option.value)}
              label={KB_AGENT_WRITE_LABEL[option.value]}
              hint={option.hint}
            />
          ))}
        </fieldset>
        {error ? <p className="text-sm text-danger">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

function Option({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean
  onChange: () => void
  label: string
  hint: string
}) {
  return (
    <label
      className={`flex cursor-pointer items-start gap-3 rounded-control border px-3 py-2.5 ${
        checked ? 'border-moss bg-moss-soft' : 'border-line hover:bg-sunken'
      }`}
    >
      <input
        type="radio"
        name="kb-agent-write"
        className="mt-1 text-moss focus:ring-moss/30"
        checked={checked}
        onChange={onChange}
      />
      <span>
        <span className="block text-base font-medium text-ink">{label}</span>
        <span className="block text-sm text-ink-3">{hint}</span>
      </span>
    </label>
  )
}
